import { useLazyQuery } from '@apollo/client'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { groupByHelper } from '../../lib/helpers'
import { MasaAccount, MasaConnectionProvider } from '../../models'
import { GET_ACCOUNTS } from './queries'

export interface UseAccountsProps {
    id?: string
    type?: MasaConnectionProvider
    /** Optionaly group by some property */
    groupBy?: string[]
    currency?: string | null
}

export type UseAccountsReturn = {
    accounts: MasaAccount[]
    groupedByAccounts: any
    accountsType: string
    userHasAnAccount: boolean
    loading: boolean
    getAdminAccounts: () => void
    getAccounts: () => void
    getTypeAccounts: () => void
}

export function useAccounts({
    id,
    type,
    currency,
    groupBy = [],
}: UseAccountsProps) {
    const [longPullingAccounts, setLongPullingAccounts] = useState<
        null | number
    >(null)
    const accountsType = useMemo(() => {
        if (type === undefined) return type
        if (type === MasaConnectionProvider.PLAID) return 'PLAID'
        if (type === MasaConnectionProvider.MASA) return 'MASA'
        else return 'VEZGO'
    }, [type])

    const [getAccounts, { data, loading, error }] = useLazyQuery(GET_ACCOUNTS, {
        fetchPolicy: 'network-only',
    })

    const accounts = useMemo(() => {
        if (!loading && data && data.listMasaAccounts)
            return data.listMasaAccounts.items
        return []
    }, [data, loading])

    const userHasAnAccount = useMemo(() => {
        return !!accounts.length
    }, [accounts])

    const getAdminAccounts = useCallback(() => {
        getAccounts({
            variables: {
                input: { id },
            },
        })
    }, [getAccounts, id])

    const getTypeAccounts = useCallback(() => {
        getAccounts({
            variables: {
                input: { accountType: accountsType, currencyTickers: currency },
            },
        })
    }, [getAccounts, accountsType, currency])

    useEffect(() => {
        getTypeAccounts()
    }, [getTypeAccounts])

    const isLongPullingAccounts = useMemo(() => {
        return loading && longPullingAccounts !== null
    }, [longPullingAccounts, loading])

    useEffect(() => {
        if (
            !loading &&
            longPullingAccounts !== null &&
            longPullingAccounts === accounts.length
        ) {
            getTypeAccounts()
        } else if (
            !loading &&
            longPullingAccounts !== null &&
            longPullingAccounts > accounts
        ) {
            setLongPullingAccounts(null)
        }
    }, [longPullingAccounts, accounts, loading, getTypeAccounts])

    const groupedByAccounts = useMemo(() => {
        if (groupBy.length) {
            return groupByHelper(accounts, groupBy)
        } else {
            return []
        }
    }, [accounts, groupBy])

    return {
        accounts,
        groupedByAccounts,
        userHasAnAccount,
        accountsType,
        getAccounts,
        getTypeAccounts,
        getAdminAccounts,
        error,
        loading,
        isLongPullingAccounts,
        setLongPulling: setLongPullingAccounts,
    }
}
